// frontend/src/pages/company/WarehouseDetails.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { warehouseAPI } from '../../services/api';

const WarehouseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedShop, setSelectedShop] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    warehouse_name: '',
    address: '',
    status: 'active'
  });

  useEffect(() => {
    fetchWarehouseDetails();
  }, [id, selectedShop]);

  const fetchWarehouseDetails = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedShop) {
        params.shop_id = selectedShop;
      }
      const response = await warehouseAPI.getById(id, params);
      setWarehouse(response.data.data);
      
      // Form data анх удаа set хийх
      if (!showEditModal) {
        setFormData({
          warehouse_name: response.data.data.warehouse_name || '',
          address: response.data.data.address || '',
          status: response.data.data.status || 'active'
        });
      }
    } catch (error) {
      console.error('Fetch warehouse details error:', error);
      alert('Агуулахын мэдээлэл татахад алдаа гарлаа');
      navigate('/company/warehouses');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setFormData({
      warehouse_name: warehouse.warehouse_name || '',
      address: warehouse.address || '',
      status: warehouse.status || 'active'
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await warehouseAPI.update(id, formData);
      alert('Агуулах амжилттай шинэчлэгдлээ');
      setShowEditModal(false);
      fetchWarehouseDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Шинэчлэхэд алдаа гарлаа');
    }
  };

  const handleDelete = async () => {
    if (!warehouse.can_delete) {
      alert('Энэ агуулахад онлайн шоп эсвэл бараа бүртгэлтэй байгаа тул устгах боломжгүй!');
      return;
    }
    
    if (!confirm('Энэ агуулахыг устгахдаа итгэлтэй байна уу?')) return;
    
    try {
      await warehouseAPI.delete(id);
      alert('Агуулах амжилттай устлаа');
      navigate('/company/warehouses');
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN').format(amount || 0) + '₮';
  };

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </CompanyLayout>
    );
  }

  if (!warehouse) {
    return (
      <CompanyLayout>
        <div className="text-center py-12">
          <h3 className="text-xl text-gray-900">Агуулах олдсонгүй</h3>
        </div>
      </CompanyLayout>
    );
  }

  const { stats, shops } = warehouse;

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/company/warehouses')}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <span>🏭</span>
                <span>{warehouse.warehouse_name}</span>
              </h1>
              <p className="text-gray-600 mt-1">{warehouse.address || 'Хаяг заагаагүй'}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              warehouse.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {warehouse.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}
            </span>
            
            {/* Edit Button */}
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Засах
            </button>
            
            {/* Delete Button */}
            <button
              onClick={handleDelete}
              disabled={!warehouse.can_delete}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                warehouse.can_delete 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              title={!warehouse.can_delete ? 'Онлайн шоп эсвэл бараа бүртгэлтэй тул устгах боломжгүй' : ''}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Устгах
            </button>
          </div>
        </div>

        {/* SHOP FILTER */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">🛍️ Онлайн шопоор шүүх</label>
            <select
              value={selectedShop}
              onChange={(e) => setSelectedShop(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">Бүх онлайн шоп</option>
              {shops && shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.shop_name}
                </option>
              ))}
            </select>
          </div>
          {selectedShop && (
            <p className="text-sm text-blue-600 mt-2">
              📍 {shops.find(s => s.id === parseInt(selectedShop))?.shop_name} шопын мэдээлэл харагдаж байна
            </p>
          )}
        </div>

        {/* STATISTICS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* ОНЛАЙН ШОП */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Онлайн шоп</p>
                <p className="text-3xl font-bold mt-1">{stats?.shop_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🛍️</span>
              </div>
            </div>
            <p className="text-blue-200 text-xs mt-2">Нийт бүртгэлтэй шоп</p>
          </div>

          {/* БАРААНЫ ТӨРӨЛ */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Барааны төрөл</p>
                <p className="text-3xl font-bold mt-1">{stats?.product_types || 0}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📦</span>
              </div>
            </div>
            <p className="text-purple-200 text-xs mt-2">Төрөл бараа</p>
          </div>

          {/* НИЙТ ТОО ХЭМЖЭЭ */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Нийт тоо хэмжээ</p>
                <p className="text-3xl font-bold mt-1">{(stats?.total_stock || 0).toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
            </div>
            <p className="text-green-200 text-xs mt-2">Ширхэг бараа</p>
          </div>

          {/* НИЙТ ҮНИЙН ДҮН */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Нийт үнийн дүн</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(stats?.total_value)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">💰</span>
              </div>
            </div>
            <p className="text-orange-200 text-xs mt-2">Барааны үнэлгээ</p>
          </div>
        </div>

        {/* INFO CARD */}
        {/* {!warehouse.can_delete && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-medium text-yellow-800">Устгах боломжгүй</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Энэ агуулахад {stats?.shop_count || 0} онлайн шоп, {stats?.product_types || 0} төрлийн бараа бүртгэлтэй байгаа тул устгах боломжгүй.
                  Эхлээд бүх бараа болон онлайн шопыг өөр агуулахад шилжүүлнэ үү.
                </p>
              </div>
            </div>
          </div>
        )} */}

        {/* REGISTERED SHOPS LIST */}
        {shops && shops.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>🛍️</span>
              <span>Бүртгэлтэй онлайн шопууд ({shops.length})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {shops.map((shop) => (
                <div 
                  key={shop.id} 
                  className={`flex items-center gap-3 p-3 rounded-lg border transition cursor-pointer ${
                    selectedShop === String(shop.id) 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedShop(selectedShop === String(shop.id) ? '' : String(shop.id))}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">🛍️</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{shop.shop_name}</p>
                    <p className={`text-xs ${shop.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                      {shop.status === 'active' ? '● Идэвхтэй' : '○ Идэвхгүй'}
                    </p>
                  </div>
                  {selectedShop === String(shop.id) && (
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>🏭</span>
              <span>Агуулах засах</span>
            </h2>
            
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Агуулахын нэр *
                </label>
                <input 
                  type="text" 
                  value={formData.warehouse_name} 
                  onChange={(e) => setFormData({ ...formData, warehouse_name: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  required 
                  placeholder="Агуулахын нэр" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Хаяг
                </label>
                <textarea 
                  value={formData.address} 
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  rows="3" 
                  placeholder="Дэлгэрэнгүй хаяг"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Статус</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="active" 
                      checked={formData.status === 'active'} 
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                      className="mr-2" 
                    />
                    <span className="text-sm">Идэвхтэй</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="inactive" 
                      checked={formData.status === 'inactive'} 
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                      className="mr-2" 
                    />
                    <span className="text-sm">Идэвхгүй</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)} 
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Болих
                </button>
                <button 
                  type="submit" 
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
                >
                  ✅ Хадгалах
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default WarehouseDetails;